import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { Helmet } from "../utils/Helmet";
import { ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        setStatus("ok");
      })
      .catch(() => setStatus("missing"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#F4F4F1]">
        <Navbar />
        <div className="max-w-[800px] mx-auto px-6 py-32 font-mono-accent text-sm text-[#4A5550]">
          Loading article…
        </div>
        <Footer />
      </div>
    );
  }

  if (status === "missing") {
    return (
      <div className="min-h-screen bg-[#F4F4F1]">
        <Navbar />
        <div className="max-w-[800px] mx-auto px-6 py-32 text-center">
          <h1 className="font-display font-black uppercase text-4xl">
            Article not found
          </h1>
          <p className="mt-4 text-[#4A5550]">
            This post may have been moved or unpublished.
          </p>
          <button
            onClick={() => nav("/blog")}
            className="mt-8 inline-flex items-center gap-2 bg-[#122A1E] text-white px-5 py-3 font-display font-bold uppercase text-sm tracking-wider border-2 border-[#122A1E]"
            data-testid="blog-back"
          >
            <ArrowLeft size={16} /> Back to blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const url = `https://marinerec-hauling.ca/blog/${post.slug}`;
  const ld = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: post.cover_image || undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    mainEntityOfPage: url,
    author: { "@type": "Organization", name: "Marine & Recreational Hauling" },
    publisher: {
      "@type": "Organization",
      name: "Marine & Recreational Hauling",
      logo: {
        "@type": "ImageObject",
        url: "https://static.prod-images.emergentagent.com/jobs/198c806a-40b0-4047-b83f-e4865cd7a905/images/84d78beca6baeed6950360fee2a1e39e868d2da3e72df1b3ce147e5fba14a4d5.png",
      },
    },
  };

  return (
    <div className="min-h-screen bg-[#F4F4F1]">
      <Helmet
        title={post.title}
        description={post.meta_description || post.excerpt}
        canonical={url}
        ogImage={post.cover_image}
        jsonLd={ld}
      />
      <Navbar />
      <main>
        <article className="max-w-[820px] mx-auto px-6 md:px-10 py-12 md:py-20" data-testid="blog-article">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase text-[#4A5550] hover:text-[#FF5A00] mb-10"
            data-testid="blog-back-link"
          >
            <ArrowLeft size={14} /> Back to blog
          </Link>

          <div className="font-mono-accent text-[11px] uppercase text-[#FF5A00] mb-3">
            ◆ {post.tag || "guide"} · {new Date(post.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-6xl text-[#0A0E0C]">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-6 text-[#4A5550] text-lg md:text-xl leading-relaxed border-l-4 border-[#FF5A00] pl-4">
              {post.excerpt}
            </p>
          )}
          {post.cover_image && (
            <div className="mt-10 border-2 border-[#122A1E] overflow-hidden">
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          )}

          <div className="mt-10 prose-mr">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          <div className="mt-14 border-2 border-[#122A1E] bg-[#122A1E] text-[#F4F4F1] p-8 hard-shadow-orange">
            <div className="font-mono-accent text-[10px] uppercase text-[#FF5A00] mb-2">
              ◆ Ready to Book?
            </div>
            <h3 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight">
              Get a Direct-Carrier Quote.
            </h3>
            <p className="mt-3 text-[#F4F4F1]/80">
              Firm price within 24 hours. No broker uplift. Coast-to-coast Canada.
            </p>
            <Link
              to="/#quote"
              className="mt-5 inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-display font-bold uppercase text-sm tracking-wider px-6 py-3 border-2 border-[#F4F4F1]"
              data-testid="blog-cta-quote"
            >
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
